import React, { useState } from 'react';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';

interface Props {
  dealId: string;
}

interface ImpactData {
  employeeCount: number;
  averageEmployeeTenureYears: number;
  hasRetirementPlan: boolean;
  workplaceAccidentsLast3yr: number;
  localSupplyChainPercent: number;
  certifications: string[];
  litigationLast5yr: boolean;
  communityInvolvement: boolean;
}

const empty: ImpactData = {
  employeeCount: 0,
  averageEmployeeTenureYears: 0,
  hasRetirementPlan: false,
  workplaceAccidentsLast3yr: 0,
  localSupplyChainPercent: 0,
  certifications: [],
  litigationLast5yr: false,
  communityInvolvement: false,
};

const UpdateImpactDataDialog: React.FC<Props> = ({ dealId }) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<ImpactData>(empty);
  const [certsText, setCertsText] = useState('');

  const tr = {
    en: {
      btn: 'Update Impact Data', title: 'Update Impact Data',
      employees: 'Number of employees', tenure: 'Average employee tenure (years)',
      retirement: 'Active retirement plan?', accidents: 'Workplace accidents (last 3 years)',
      supply: 'Local supply chain (%)', cert: 'Certifications (comma-separated)',
      lit: 'Any litigation in the last 5 years?', community: 'Active community involvement?',
      save: 'Save', cancel: 'Cancel', saved: 'Impact data updated',
    },
    fr: {
      btn: 'Mettre à jour Impact', title: 'Mettre à jour les données d\'impact',
      employees: 'Nombre d\'employés', tenure: 'Ancienneté moyenne (années)',
      retirement: 'Régime de retraite actif?', accidents: 'Accidents (3 dernières années)',
      supply: 'Chaîne locale (%)', cert: 'Certifications (séparées par virgules)',
      lit: 'Litiges au cours des 5 dernières années?', community: 'Implication communautaire active?',
      save: 'Enregistrer', cancel: 'Annuler', saved: 'Données d\'impact mises à jour',
    },
  }[language as 'en' | 'fr'] || ({} as any);

  const loadData = async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'deals', dealId));
      if (snap.exists()) {
        const d = snap.data() as any;
        const next: ImpactData = {
          employeeCount: d.employeeCount ?? 0,
          averageEmployeeTenureYears: d.averageEmployeeTenureYears ?? 0,
          hasRetirementPlan: !!d.hasRetirementPlan,
          workplaceAccidentsLast3yr: d.workplaceAccidentsLast3yr ?? 0,
          localSupplyChainPercent: d.localSupplyChainPercent ?? 0,
          certifications: Array.isArray(d.certifications) ? d.certifications : [],
          litigationLast5yr: !!d.litigationLast5yr,
          communityInvolvement: !!d.communityInvolvement,
        };
        setData(next);
        setCertsText(next.certifications.join(', '));
      }
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (v: boolean) => {
    setOpen(v);
    if (v) loadData();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const certifications = certsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = { ...data, certifications };
      const uid = auth.currentUser?.uid || null;

      await updateDoc(doc(db, 'deals', dealId), {
        ...payload,
        impactDataVerified: false,
        impactDataSource: 'self_declared',
        impactVerificationLevel: 'none',
        updatedAt: serverTimestamp(),
        revisionHistory: arrayUnion({
          section: 'impact',
          updatedBy: uid,
          updatedAt: new Date().toISOString(),
          changes: payload,
        }),
      });

      await addDoc(collection(db, 'admin_tasks'), {
        type: 'impact_data_updated',
        dealId,
        updatedBy: uid,
        status: 'pending_review',
        createdAt: serverTimestamp(),
      });

      toast({ title: tr.saved });
      setOpen(false);
    } catch (e) {
      console.error(e);
      toast({ title: 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof ImpactData>(k: K, v: ImpactData[K]) =>
    setData((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <RefreshCw className="h-4 w-4 mr-1" /> {tr.btn}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{tr.title}</DialogTitle></DialogHeader>
        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>{tr.employees}</Label>
              <Input type="number" min={0} value={data.employeeCount}
                onChange={(e) => set('employeeCount', Number(e.target.value))} />
            </div>
            <div>
              <Label>{tr.tenure}</Label>
              <Input type="number" min={0} step="0.1" value={data.averageEmployeeTenureYears}
                onChange={(e) => set('averageEmployeeTenureYears', Number(e.target.value))} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="ret" checked={data.hasRetirementPlan}
                onCheckedChange={(v) => set('hasRetirementPlan', !!v)} />
              <Label htmlFor="ret">{tr.retirement}</Label>
            </div>
            <div>
              <Label>{tr.accidents}</Label>
              <Input type="number" min={0} value={data.workplaceAccidentsLast3yr}
                onChange={(e) => set('workplaceAccidentsLast3yr', Number(e.target.value))} />
            </div>
            <div>
              <Label>{tr.supply}</Label>
              <Input type="number" min={0} max={100} value={data.localSupplyChainPercent}
                onChange={(e) => set('localSupplyChainPercent', Number(e.target.value))} />
            </div>
            <div>
              <Label>{tr.cert}</Label>
              <Textarea value={certsText} onChange={(e) => setCertsText(e.target.value)} rows={2} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="lit" checked={data.litigationLast5yr}
                onCheckedChange={(v) => set('litigationLast5yr', !!v)} />
              <Label htmlFor="lit">{tr.lit}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="com" checked={data.communityInvolvement}
                onCheckedChange={(v) => set('communityInvolvement', !!v)} />
              <Label htmlFor="com">{tr.community}</Label>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{tr.cancel}</Button>
          <Button onClick={handleSave} disabled={saving || loading}>{tr.save}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateImpactDataDialog;
